/** The roster file as the panel reads it - one row per roster row, with state. */

export type RosterRowState = "done" | "error" | "not_run";
export type RosterFileFlag =
  | "no_id"
  | "no_identity"
  | "duplicate"
  | "ambiguous";
export type RosterFileState = "all" | RosterRowState | RosterFileFlag;

export interface RosterFileInfo {
  /** `roster` is employees_info.json; `matches` is the upload job's CSV. */
  kind: "roster" | "matches";
  filename: string;
  key: string | null;
  exists: boolean;
  size_bytes: number | null;
  last_modified: string | null;
  rows: number | null;
  sibling_job_ids: number[];
}

export interface RosterFileRowDto {
  index: number;
  employee_name: string | null;
  employee_id: string | null;
  s3_name: string | null;
  /** Whom the bot's log names on a row the roster keeps only the link of. */
  logged_employee_name: string | null;
  logged_employee_id: string | null;
  state: RosterRowState;
  task_id: number | null;
  attempts: number;
  resolved: boolean;
  manual: boolean;
  from_deleted_task: boolean;
  employee_status: string | null;
  level: string | null;
  last_attempt_at: string | null;
  first_failed_at: string | null;
  flags: RosterFileFlag[];
  /** The rest of the row as the file spells it, SSN-shaped values masked. */
  fields: Record<string, string>;
}

export interface RosterFileCounts {
  total: number;
  done: number;
  error: number;
  not_run: number;
  /** The job's own question: people, not rows. A roster may list one twice. */
  employees_done: number;
  employees_error: number;
  records_not_in_roster: number;
  rows_by_position: number;
  no_id: number;
  no_identity: number;
  duplicate: number;
  ambiguous: number;
}

export interface RosterFileRecordsResponse {
  counts: RosterFileCounts;
  records: RosterFileRowDto[];
  total_matched: number;
  offset: number;
  limit: number;
  has_more: boolean;
  /** No record matched by identity, so the rows were matched by position. */
  positional: boolean;
  revealed: boolean;
  generated_at: string;
  file: RosterFileInfo;
  can_reveal: boolean;
}

export interface RosterFileQuery {
  jobId: number;
  state?: RosterFileState;
  q?: string;
  limit?: number;
  offset?: number;
  reveal?: boolean;
}

export const ROSTER_STATE_LABELS: Record<RosterRowState, string> = {
  done: "Done",
  error: "Error",
  not_run: "Not run yet",
};

export const ROSTER_FLAG_LABELS: Record<RosterFileFlag, string> = {
  no_id: "No id",
  no_identity: "No identity",
  duplicate: "Duplicate row",
  ambiguous: "Ambiguous",
};

export const ROSTER_LOGGED_IDENTITY_HINT =
  "The roster keeps only this row's link (its number stands on another row too), " +
  "so this is who the bot found on the portal page";

export const ROSTER_FLAG_TOOLTIPS: Record<RosterFileFlag, string> = {
  no_id: "The row carries no vendor id, so its records are matched by name",
  no_identity:
    "The roster names nobody on this row, so it is matched by its position; " +
    "the name shown is the one the bot logged there",
  duplicate: "This row repeats another row of the file field for field",
  ambiguous:
    "This employee stands on another row too, and nothing the logs carry - a " +
    "folder, a company - tells those rows apart, so no record can say which of " +
    "them is done and every run walks all of them",
};

export const ROSTER_FILE_FILTERS: Array<{
  value: RosterFileState;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "done", label: ROSTER_STATE_LABELS.done },
  { value: "error", label: ROSTER_STATE_LABELS.error },
  { value: "not_run", label: ROSTER_STATE_LABELS.not_run },
  { value: "ambiguous", label: ROSTER_FLAG_LABELS.ambiguous },
  { value: "duplicate", label: ROSTER_FLAG_LABELS.duplicate },
  { value: "no_id", label: ROSTER_FLAG_LABELS.no_id },
  { value: "no_identity", label: ROSTER_FLAG_LABELS.no_identity },
];

export function buildRosterFileQuery(params: RosterFileQuery): string {
  const search = new URLSearchParams();
  if (params.state && params.state !== "all")
    search.append("state", params.state);
  if (params.q) search.append("q", params.q);
  if (params.limit != null) search.append("limit", String(params.limit));
  if (params.offset) search.append("offset", String(params.offset));
  if (params.reveal) search.append("reveal", "1");
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function rosterFileLabel(
  info: RosterFileInfo | undefined | null,
): string {
  if (!info) return "Roster";
  return info.kind === "matches" ? "Matches rows" : "Roster";
}

/** A roster row's number as the panel writes it: an index of the file, not an id. */
export function rosterRowLabel(row: number): string {
  return `row ${row}`;
}

export const ROSTER_ROW_HINT =
  "The row's place in the job's roster file (the Matches file on an upload " +
  "job), counted from 0 - the number the bots call the inner number. It is not " +
  "a vendor id: a rebuilt roster can put an employee on another row.";

/** The row chip of a log record whose employee the roster still holds. */
export const LOG_ROW_HINT =
  "The employee's row in the job's roster file, counted from 0 - not a vendor " +
  "id. The record's text calls it the inner number and keeps the row the run " +
  "walked; after a rebuild the employee can stand on another row, and this " +
  "chip follows them.";

/** The row chip of a log record whose employee the roster no longer holds. */
export const LOG_ROW_GONE_HINT =
  "The row this employee stood on when the run wrote this record, counted from " +
  "0 - not a vendor id. The roster file no longer holds them.";

export const LOG_RANGE_HINT =
  "Rows of the roster file, counted from 0 - the numbers on the records' row " +
  "chips, not vendor ids.";

export const EMPTY_ROSTER_MESSAGE =
  "The stored roster holds no rows - the last collection found nobody. Run " +
  "Build roster only to collect it again, or upload one by hand.";

export const MISSING_ROSTER_MESSAGE =
  "No roster is stored for this job yet. The next run collects it, and Build " +
  "roster only collects it without processing anybody.";

export const POSITIONAL_ROSTER_MESSAGE =
  "No log record could be matched to this roster by identity, so the rows are " +
  "matched by their position, as before. A roster rebuilt since the run then " +
  "answers for whoever stands on those rows now.";
